import ExcelJS from 'exceljs';
import { TICKETS_EXPORT_STYLES } from '../constants/ticketsExportStyles';

const TEMPLATE_PATH = `${process.env.PUBLIC_URL || ''}/tickets-export-template.xlsx`;

/** Resolves react-csv-style header keys (e.g. createdBy.email, comments[0].text). */
export function getCellValue(row, key) {
  if (key == null || key === '') return '';
  const segments = String(key).split(/\.|\[|\]/).filter(Boolean);
  let cur = row;
  for (const seg of segments) {
    if (cur == null) return '';
    cur = cur[seg];
  }
  if (cur == null) return '';
  if (typeof cur === 'object') return JSON.stringify(cur);
  return cur;
}

function extractHeaderText(cell) {
  if (cell.value == null || cell.value === '') return '';
  if (typeof cell.value === 'object' && cell.value.richText) {
    return cell.text != null ? String(cell.text).trim() : '';
  }
  return String(cell.value).trim();
}

function copyRowStyle(sourceRow, targetRow) {
  sourceRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const target = targetRow.getCell(colNumber);
    if (cell.font) target.font = { ...cell.font };
    if (cell.fill) target.fill = JSON.parse(JSON.stringify(cell.fill));
    if (cell.border) target.border = JSON.parse(JSON.stringify(cell.border));
    if (cell.alignment) target.alignment = { ...cell.alignment };
    if (cell.numFmt) target.numFmt = cell.numFmt;
  });
  if (sourceRow.height) targetRow.height = sourceRow.height;
}

function buildColumnMap(ws, fileHeaders) {
  const headerRow = ws.getRow(1);
  const byLabel = new Map();
  headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    const text = extractHeaderText(cell);
    if (text) byLabel.set(text, colNumber);
  });
  return fileHeaders.map((h, idx) => {
    const col = byLabel.get(h.label.trim());
    return col != null ? col : idx + 1;
  });
}

function applyBodyStyle(cell, rowIndex, useZebra) {
  const s = TICKETS_EXPORT_STYLES;
  cell.font = { ...s.body.font };
  cell.alignment = { ...s.body.alignment };
  cell.border = JSON.parse(JSON.stringify(s.body.border));
  if (useZebra && rowIndex % 2 === 1) {
    cell.fill = JSON.parse(JSON.stringify(s.zebraFill));
  }
}

function toKey(value) {
  return String(value ?? '').trim().toUpperCase();
}

function cloneStyle(styleObj) {
  return JSON.parse(JSON.stringify(styleObj));
}

const CENTER_ALIGN_KEYS = new Set([
  'shortseqid',
  'name',
  'organization.name',
  'cts',
  'mts',
]);

const BOLD_KEYS = new Set(['shortseqid', 'name']);

function applyColumnPresentation(cell, header) {
  const key = String(header || '').trim().toLowerCase();
  if (CENTER_ALIGN_KEYS.has(key)) {
    cell.alignment = {
      ...(cell.alignment || {}),
      horizontal: 'center',
      vertical: 'middle',
      wrapText: true,
    };
  }
  if (BOLD_KEYS.has(key)) {
    cell.font = { ...(cell.font || {}), bold: true };
  }
}

function applyConditionalValueStyle(cell, header, value) {
  const rules = TICKETS_EXPORT_STYLES.valueStyles || {};
  const normalizedHeader = String(header || '').trim().toLowerCase();
  const lookup = rules[normalizedHeader];
  if (!lookup) return;
  const rule = lookup[toKey(value)];
  if (!rule) return;
  if (rule.fill) {
    cell.fill = cloneStyle(rule.fill);
  }
  if (rule.font) {
    cell.font = { ...(cell.font || {}), ...rule.font };
  }
  cell.alignment = {
    ...(cell.alignment || {}),
    horizontal: 'center',
    vertical: 'middle',
    wrapText: true,
  };
}

async function fillFromTemplate(buffer, data, fileHeaders) {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const ws = workbook.getWorksheet(1) || workbook.worksheets[0];
  if (!ws) {
    throw new Error('Template has no worksheet');
  }

  const colMap = buildColumnMap(ws, fileHeaders);
  const maxCol = Math.max(1, ...colMap, fileHeaders.length);
  const sampleRow = ws.getRow(2);
  const hasSample = [1, 2, 3, 4, 5, 6, 7, 8].some((c) => {
    if (c > maxCol) return false;
    const cell = sampleRow.getCell(c);
    return (
      (cell.value != null && cell.value !== '') ||
      cell.font != null ||
      cell.fill != null
    );
  });

  for (let i = 0; i < data.length; i += 1) {
    const row = ws.getRow(2 + i);
    if (i > 0 && hasSample) {
      copyRowStyle(sampleRow, row);
    } else if (!hasSample) {
      for (let c = 1; c <= maxCol; c += 1) {
        applyBodyStyle(row.getCell(c), i, true);
      }
      row.height = TICKETS_EXPORT_STYLES.dataRowHeight;
    }
    fileHeaders.forEach((h, j) => {
      const col = colMap[j];
      const value = getCellValue(data[i], h.key);
      const cell = row.getCell(col);
      cell.value = value;
      applyColumnPresentation(cell, h.key);
      applyConditionalValueStyle(cell, h.key, value);
    });
  }

  const firstExtraRow = 2 + data.length;
  if (typeof ws.rowCount === 'number' && ws.rowCount >= firstExtraRow) {
    const toRemove = ws.rowCount - firstExtraRow + 1;
    if (toRemove > 0) {
      ws.spliceRows(firstExtraRow, toRemove);
    }
  }

  return workbook;
}

function buildFromScratch(data, fileHeaders) {
  const s = TICKETS_EXPORT_STYLES;
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet(s.sheetName, {
    views: [{ state: 'frozen', ySplit: 1 }],
  });

  const headerRow = ws.addRow(fileHeaders.map((h) => h.label));
  headerRow.height = s.headerRowHeight;
  headerRow.eachCell((cell) => {
    cell.font = { ...s.header.font };
    cell.fill = JSON.parse(JSON.stringify(s.header.fill));
    cell.alignment = { ...s.header.alignment };
    cell.border = JSON.parse(JSON.stringify(s.header.border));
  });

  data.forEach((dataRow, rowIndex) => {
    const row = ws.addRow(fileHeaders.map((h) => getCellValue(dataRow, h.key)));
    row.height = s.dataRowHeight;
    row.eachCell((cell, colNumber) => {
      applyBodyStyle(cell, rowIndex, true);
      const header = fileHeaders[colNumber - 1]?.key;
      applyColumnPresentation(cell, header);
      applyConditionalValueStyle(cell, header, cell.value);
    });
  });

  fileHeaders.forEach((_, i) => {
    const w = s.columnWidths[i] ?? s.defaultColumnWidth;
    ws.getColumn(i + 1).width = w;
  });

  return workbook;
}

function triggerDownload(buffer, filename) {
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function exportTicketsToExcel(data, fileHeaders) {
  let workbook;
  try {
    const res = await fetch(TEMPLATE_PATH, { method: 'GET' });
    if (res.ok) {
      const buf = await res.arrayBuffer();
      workbook = await fillFromTemplate(buf, data, fileHeaders);
    } else {
      workbook = buildFromScratch(data, fileHeaders);
    }
  } catch {
    workbook = buildFromScratch(data, fileHeaders);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(buffer, 'Tickets.xlsx');
}
